import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePreviousDistinct } from 'react-use';

type ActiveMenuItem = {
  to?: string;
  /** Additional paths whose descendants select this item without changing its destination. */
  activePaths?: readonly string[];
};

export function findActiveItemByPath<T extends ActiveMenuItem>(
  items: T[],
  pathname: string
): T | undefined {
  const locationSegments = pathname.split('/').filter(Boolean);
  return items.find((item) => {
    if (!item.to) return false;
    // Nav items can include query params (e.g. /internal/dev-tools?tab=...).
    // Active matching should use the path only.
    // split() always returns at least one segment, but TS can't infer it with
    // noUncheckedIndexedAccess enabled.
    return [item.to, ...(item.activePaths ?? [])].some((path) => {
      const [itemPath = ''] = path.split(/[?#]/);
      const itemSegments = itemPath.split('/').filter(Boolean);
      return itemSegments.every(
        (segment, index) => locationSegments[index] === segment
      );
    });
  });
}

export function useActiveMenuItem<T extends ActiveMenuItem>(
  items: T[],
  extraItem?: T | { to?: string }
) {
  const location = useLocation();
  const allItems = useMemo(
    () => [...items, ...(extraItem ? [extraItem as T] : [])],
    [items, extraItem]
  );

  const active = useMemo(
    () => findActiveItemByPath(allItems, location.pathname),
    [allItems, location.pathname]
  );

  const prev = usePreviousDistinct(active);

  return { active, prev, fallback: items[0] };
}
